import { useMutation, useQuery, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { simpleSalesApi } from "@/lib/api";
import { useOrganizationStore } from "@/stores/organizationStore";
import type { CreateSimpleSalesEntry, SimpleSalesEntry } from "@/types";

interface SimpleSalesFilters {
  search?: string;
  pageSize?: number;
  enabled?: boolean;
}

export function useSimpleSalesEntries({
  search,
  pageSize = 100,
  enabled = true,
}: SimpleSalesFilters = {}) {
  const { currentLabel } = useOrganizationStore();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["simple-sales", currentLabel?.id, { search: search?.trim() || null, pageSize }],
    queryFn: () =>
      simpleSalesApi.getEntries({
        label_id: currentLabel?.id,
        q: search?.trim() || undefined,
        page_size: pageSize,
      }),
    enabled: enabled && !!currentLabel,
    placeholderData: keepPreviousData,
  });

  return {
    entries: data?.results ?? [],
    count: data?.count ?? 0,
    isLoading,
    refetch,
  };
}

export function useSimpleSalesEntry(entryId: string | undefined) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["simple-sales-entry", entryId],
    queryFn: () => simpleSalesApi.getEntry(entryId as string),
    enabled: !!entryId,
  });

  return { entry: data, isLoading, refetch };
}

export function useCreateSimpleSalesEntry() {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: (entry: CreateSimpleSalesEntry) => simpleSalesApi.createEntry(entry),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["simple-sales"] });
    },
  });
  return { createEntry: mutate, isPending };
}

export function usePatchSimpleSalesEntry() {
  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<CreateSimpleSalesEntry> }) =>
      simpleSalesApi.patchEntry(id, updates),
    onSuccess: (entry: SimpleSalesEntry) => {
      queryClient.invalidateQueries({ queryKey: ["simple-sales"] });
      queryClient.invalidateQueries({ queryKey: ["simple-sales-entry", entry.id] });
    },
  });
  return { patchEntry: mutate, isPending };
}

export function useDeleteSimpleSalesEntry() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (id: string) => simpleSalesApi.deleteEntry(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["simple-sales"] });
      queryClient.removeQueries({ queryKey: ["simple-sales-entry", id] });
    },
  });
  return { deleteEntry: mutateAsync, isPending };
}
